"use client";
import Link from "next/link";
import { ActionForm, SubmitButton, type FormAction } from "@/components/ui/client";
import { Checkbox, Field, Input } from "@/components/ui";
import { useT } from "@/i18n/client";

export function LoginForm({ action, next }: { action: FormAction; next?: string }) {
  const t = useT();
  return (
    <ActionForm action={action} className="space-y-4">
      {(s) => (
        <>
          <input type="hidden" name="next" value={next ?? ""} />
          <Field label={t("auth.fieldEmail")} htmlFor="email" error={s.fieldErrors?.email}>
            <Input id="email" name="email" type="email" autoComplete="email" required autoFocus />
          </Field>
          <Field label={t("auth.fieldPassword")} htmlFor="password" error={s.fieldErrors?.password}>
            <Input id="password" name="password" type="password" autoComplete="current-password" required />
          </Field>
          <div className="flex justify-end text-sm">
            <Link href="/forgot-password" className="font-medium text-brand-700 hover:underline">{t("auth.forgotLink")}</Link>
          </div>
          <SubmitButton className="w-full" size="lg" pendingText={t("auth.signingIn")}>{t("auth.signIn")}</SubmitButton>
        </>
      )}
    </ActionForm>
  );
}

export function RegisterForm({ action, next }: { action: FormAction; next?: string }) {
  const t = useT();
  const [beforeTerms, rest = ""] = t("auth.termsAgree").split("{terms}");
  const [between, afterPrivacy = ""] = rest.split("{privacy}");
  return (
    <ActionForm action={action} className="space-y-4">
      {(s) => (
        <>
          <input type="hidden" name="next" value={next ?? ""} />
          <Field label={t("auth.fieldName")} htmlFor="name" error={s.fieldErrors?.name}>
            <Input id="name" name="name" autoComplete="name" required autoFocus />
          </Field>
          <Field label={t("auth.fieldEmail")} htmlFor="email" error={s.fieldErrors?.email}>
            <Input id="email" name="email" type="email" autoComplete="email" required />
          </Field>
          <Field label={t("auth.fieldPassword")} htmlFor="password" error={s.fieldErrors?.password} hint={t("auth.passwordHint")}>
            <Input id="password" name="password" type="password" autoComplete="new-password" minLength={10} required />
          </Field>
          <Checkbox name="terms" required label={<>{beforeTerms}<Link href="/legal/terms" className="text-brand-700 underline">{t("auth.termsLink")}</Link>{between}<Link href="/legal/privacy" className="text-brand-700 underline">{t("auth.privacyLink")}</Link>{afterPrivacy}</>} />
          {s.fieldErrors?.terms && <p className="text-sm text-red-600">{s.fieldErrors.terms}</p>}
          <SubmitButton className="w-full" size="lg" pendingText={t("auth.creatingAccount")}>{t("auth.createAccount")}</SubmitButton>
        </>
      )}
    </ActionForm>
  );
}

export function ForgotForm({ action }: { action: FormAction }) {
  const t = useT();
  return (
    <ActionForm action={action} className="space-y-4" resetOnSuccess>
      {(s) => (
        <>
          <Field label={t("auth.fieldEmail")} htmlFor="email" error={s.fieldErrors?.email}>
            <Input id="email" name="email" type="email" autoComplete="email" required autoFocus />
          </Field>
          <SubmitButton className="w-full" size="lg" pendingText={t("auth.sending")}>{t("auth.sendResetLink")}</SubmitButton>
        </>
      )}
    </ActionForm>
  );
}

export function ResetForm({ action, token }: { action: FormAction; token: string }) {
  const t = useT();
  return (
    <ActionForm action={action} className="space-y-4">
      {(s) => (
        <>
          <input type="hidden" name="token" value={token} />
          <Field label={t("auth.fieldNewPassword")} htmlFor="password" error={s.fieldErrors?.password ?? s.fieldErrors?._} hint={t("auth.passwordHint")}>
            <Input id="password" name="password" type="password" autoComplete="new-password" minLength={10} required autoFocus />
          </Field>
          <Field label={t("auth.fieldConfirmPassword")} htmlFor="confirm" error={s.fieldErrors?.confirm}>
            <Input id="confirm" name="confirm" type="password" autoComplete="new-password" required />
          </Field>
          <SubmitButton className="w-full" size="lg" pendingText={t("common.saving")}>{t("auth.setNewPassword")}</SubmitButton>
        </>
      )}
    </ActionForm>
  );
}
